import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Paperclip, Send, X } from 'lucide-react';
import { useRef, useState } from 'react';

import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';
import { useToast } from '@/lib/toast-context';
import type { ConversationDetail } from '@shared/schemas/conversation';

/*
 * Replying to a customer — ROADMAP.md §9.4.
 *
 * **This control sends real mail to a real person**, which is why it looks
 * nothing like the internal-note box above it and shares no state with it.
 * There is no toggle between "note" and "reply": they are two components
 * calling two endpoints, because the entire cost of confusing them is an
 * internal remark arriving in a customer's inbox.
 *
 * The recipient is shown and is not editable. It comes from the conversation,
 * so a reply cannot be aimed anywhere else.
 */

interface SendResult {
  status: 'sent' | 'failed' | 'suppressed';
  error?: string;
  conversation: ConversationDetail;
}

export function ReplyBox({ detail }: { detail: ConversationDetail }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const fileInput = useRef<HTMLInputElement>(null);

  const [body, setBody] = useState('');
  const [files, setFiles] = useState<File[]>([]);

  const send = useMutation({
    mutationFn: () => {
      const form = new FormData();
      form.append('bodyText', body);
      for (const file of files) form.append('attachments', file);

      return api.upload<SendResult>(`/api/admin/conversations/${detail.id}/messages`, form);
    },
    onSuccess: (result) => {
      queryClient.setQueryData(['admin', 'conversations', detail.id], result.conversation);
      setBody('');
      setFiles([]);
      if (fileInput.current) fileInput.current.value = '';

      /*
       * **A refused send is reported as what it is.** The message is on the
       * thread either way, marked with the server's own words — so this says so
       * plainly rather than showing a success toast over a message that never
       * arrived.
       */
      if (result.status === 'sent') {
        toast.success('Sent.');
      } else if (result.status === 'suppressed') {
        toast.error('Not sent — that address is on the do-not-contact list.');
      } else {
        toast.error(`Not delivered${result.error === undefined ? '' : ` — ${result.error}`}`);
      }
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  return (
    <section className="border-ink-200 rounded-lg border bg-white p-4">
      <div className="flex flex-wrap items-baseline gap-x-2">
        <label htmlFor="conversation-reply" className="text-meta font-medium text-ink-900">
          Reply
        </label>
        {/* Shown, not editable — the address belongs to the thread. */}
        <span className="text-caption text-ink-500">to {detail.contactEmail}</span>
      </div>

      <textarea
        id="conversation-reply"
        value={body}
        onChange={(event) => {
          setBody(event.target.value);
        }}
        rows={5}
        placeholder="Write your reply. The customer gets this as an ordinary email and can reply straight back to it."
        /*
         * **No `focus:outline-none` here.** It was, until 13.3's sweep measured
         * it: Tailwind's `outline-none` is a *transparent* 2px outline, not the
         * absence of one, and `.class:focus` outranks the global
         * `:focus-visible` rule in styles/index.css — so tabbing into the box
         * that sends mail to a customer drew no ring at all. A border colour
         * change is not a focus indicator on its own.
         */
        className="placeholder:text-ink-400 focus:border-bark-500 mt-2 w-full rounded-sm border border-ink-300 bg-white p-3 text-body text-ink-900"
      />

      {files.length > 0 ? (
        <ul className="mt-2 flex flex-wrap gap-2">
          {files.map((file, index) => (
            <li
              key={`${file.name}-${String(index)}`}
              className="border-ink-200 text-caption flex items-center gap-1.5 rounded-full border bg-ink-50 py-1 pl-3 pr-1 text-ink-700"
            >
              {file.name}
              <button
                type="button"
                aria-label={`Remove ${file.name}`}
                onClick={() => {
                  setFiles(files.filter((_, position) => position !== index));
                }}
                // 44px minimum hit area — CLAUDE.md §4, defects P23/P24.
                className="grid size-11 place-items-center rounded-full text-ink-500 hover:text-ink-900"
              >
                <X aria-hidden="true" className="size-4" />
              </button>
            </li>
          ))}
        </ul>
      ) : null}

      <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
        {/*
          **`focus-within` is the focus ring, and it was missing until 10.1.**
          The input below is `sr-only` — `clip: rect(0,0,0,0)` — so the global
          `:focus-visible` rule genuinely applies to it and genuinely paints a
          2px outline into a box of no size. This label is what a keyboard user
          is looking at, and it drew nothing.

          13.3 measured this exact screen and passed it, because its sweep read
          the outline off the focused element and stopped there. The sweep now
          follows a clipped element up to an ancestor that can actually show a
          ring, and this is the control it found on its first run.
        */}
        <label className="flex min-h-11 cursor-pointer items-center gap-2 rounded-sm text-meta text-bark-700 focus-within:outline focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-ink-900">
          <Paperclip aria-hidden="true" className="size-4" />
          Attach files
          <input
            ref={fileInput}
            type="file"
            multiple
            className="sr-only"
            onChange={(event) => {
              setFiles([...files, ...Array.from(event.target.files ?? [])]);
            }}
          />
        </label>

        <Button
          onClick={() => {
            send.mutate();
          }}
          disabled={body.trim().length === 0 || send.isPending}
        >
          <Send aria-hidden="true" className="mr-1.5 size-4" />
          {send.isPending ? 'Sending…' : 'Send'}
        </Button>
      </div>
    </section>
  );
}
